import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography, Alert } from '@mui/material'
import { UserRole } from '@tnf-recruit/shared'
import { useUser } from '../hooks/useUser'
import { OnboardingGuard } from './OnboardingGuard'
import { useAuth } from '../hooks/useAuth'

interface ProtectedRouteProps {
  children: ReactNode
  allowedRoles?: UserRole[]
}

export const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Sync the authenticated user with the backend DB.
  // This MUST complete before children render so that downstream hooks
  // (useStudentProfile, useRecruiterProfile, etc.) find a `users` row.
  const dbUser = useUser()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  // Wait for user sync to complete
  if (dbUser.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  if (dbUser.isError) {
    // The recruiter email-domain gate explains itself in the response body;
    // without this the user is told to refresh, which will never help them.
    const serverMessage = (dbUser.error as { response?: { data?: { error?: string } } })?.response
      ?.data?.error

    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        px={3}
      >
        <Alert severity="error" sx={{ maxWidth: 560 }}>
          {serverMessage ?? 'Failed to sync your account. Please try refreshing the page.'}
        </Alert>
      </Box>
    )
  }

  // Check role-based access if specified
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = dbUser.data?.role
    if (!userRole || !allowedRoles.includes(userRole)) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          gap={2}
        >
          <Typography variant="h5" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1">You don't have permission to access this page.</Typography>
        </Box>
      )
    }
  }

  return <OnboardingGuard>{children}</OnboardingGuard>
}
